import React, {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Descendant, Element } from 'slate';
import { RenderElementProps } from 'slate-react';
import { v4 as uuid } from 'uuid';

import useVariantsByComponentIdQuery from 'hooks/components/useVariantsByComponentIdQuery';
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';
import { FormulaType } from '../../../../../constants/formulaType';
// constants
import routes from '../../../../../constants/routes';

// context
import { EditorContext } from '../../../../../context/EditorContext';

import confirm from '../../../../../helpers/confirm';
//helpers
import debounce from '../../../../../helpers/debounce';
import { newImageElement } from './helpers';

import useComponentTypesQuery from '../../../../../hooks/component-types/useComponentTypesQuery';
// hooks
import useDatasetByIdQuery from '../../../../../hooks/datasets/useDatasetByIdQuery';
import useFormulaCreateMutation from '../../../../../hooks/formulas/useFormulaCreateMutation';
import { useLockFormulaWarning } from '../../../../../hooks/formulas/useLockFormulaWarning';
import useHandleQueryParams from '../../../../../hooks/helper/useHandleQueryParams';
import useQueryParams from '../../../../../hooks/helper/useQueryParams';
import useGetCurrentMessageContent from '../../../../../hooks/message-content/useGetCurrentMessageContent';
import useMessageContentGetAndUpdate from '../../../../../hooks/message-content/useMessageContentGetAndUpdate';

// models
import { VariantUpdateModel } from '../../../../../models/alternate.model';
import {
    ElementKind,
    ImageVariantElement,
    SectionElement,
    getSlateInputType,
} from '../../../../../models/editor.model';
import {
    ContentCardType,
    EmailFieldModifiers,
    FormulaTypesData,
} from '../../../../../models/formula.model';
import { VariantType } from '../../../../../models/variant.model';

import DatasetConfigurationalModalV1 from '../../../../../views/formulaCreate/formulaEditor/assistant/datasets/datasetConfigurationalModal/DatasetConfigurationalModalV1';

//components
import ImageAlternateContainer from './ImageAlternateContainer';
import ImageInput from './ImageInput';
import ImageInputLabel from './ImageInputLabel';

interface ImageInputElementProps extends RenderElementProps {
    element: ImageVariantElement;
    handleUpdateStructure: (newValue: Descendant[]) => void;
    isDisabled?: boolean;
    label?: string;
}

const ImageInputElement: FC<ImageInputElementProps> = ({
    attributes,
    element,
    handleUpdateStructure,
    isDisabled,
    label,
}) => {
    const {
        editorDetails: { formulaType, formula },
    } = useContext(EditorContext);

    const { formulaId } = useParams<{ formulaId: string }>();
    const history = useHistory();
    const isNewFormulaRef = useRef<boolean>(false);
    const { currentMessageContentId } = useGetCurrentMessageContent(
        formula?.id,
    );
    const { data: componentTypes } = useComponentTypesQuery();

    const [queryParams] = useHandleQueryParams();

    const [showImageAlternateIconModal, setShowImageAlternateIconModal] =
        useState<boolean>(false);

    const { hasWarning } = useLockFormulaWarning();

    const { data = [] } = useVariantsByComponentIdQuery(element.id);

    const [
        showDatasetConfigurationalModal,
        setShowDatasetConfigurationalModal,
    ] = useState<boolean>(false);

    const [selectedFieldId, setSelectedFieldId] = useState<
        string | undefined
    >();

    const { message_type = FormulaType.mobilePush } = useQueryParams();

    const isEmail = useMemo<boolean>(
        () => formulaType === FormulaType.email,
        [formulaType],
    );

    const initialText = useMemo<string>(
        () => element.children[0]?.text ?? '',
        [element],
    );

    const [text, setText] = useState<string | undefined>(initialText);

    const contentCardType = useMemo<ContentCardType | undefined>(() => {
        if (message_type === FormulaType.contentCards) {
            return formula?.content_card_type || ContentCardType.TextOnly;
        }
    }, [formula, message_type]);

    const originalStructure = useMemo<Descendant[]>(
        () =>
            Number(formulaId) && formula?.structure
                ? formula.structure
                : FormulaTypesData[message_type as FormulaType].getTemplate({
                      content_card_type: contentCardType,
                  }),
        [formula, formulaId, message_type, contentCardType],
    );

    const messageContentUpdate = useMessageContentGetAndUpdate();
    const createFormula = useFormulaCreateMutation(message_type);
    const { data: datasetData } = useDatasetByIdQuery(formula?.dataset_id!);

    const inputType = getSlateInputType(element);

    const isRequired = useMemo<boolean>(() => {
        const [, ...modifiers] = inputType.split('--');
        return modifiers.includes(EmailFieldModifiers.required);
    }, [inputType]);

    const getCurrentElementIndex = useCallback(() => {
        const structure = [...(originalStructure ?? [])];
        if (
            [FormulaType.mobilePush, FormulaType.contentCards].includes(
                formulaType as FormulaType,
            )
        ) {
            return structure.findIndex(
                (item) => (item as Element).kind === ElementKind.image,
            );
        }
        return structure.findIndex((item) => {
            const ele = item as SectionElement;
            const isSection = ele.kind === ElementKind.section;
            const isImage = ele.sectionType === inputType;
            return isSection && isImage;
        });
    }, [formulaType, inputType, originalStructure]);

    useEffect(() => {
        const index = getCurrentElementIndex();
        setSelectedFieldId(
            (
                formula?.structure?.[index] as ImageVariantElement
            )?.datasetField?.toString(),
        );
    }, [getCurrentElementIndex, formula?.structure]);

    const getUpdateStructure = useCallback(
        (id: string, text: string, labelId?: number) => {
            const structure = [...(originalStructure ?? [])];
            const elementIndex = getCurrentElementIndex();
            if (elementIndex !== -1) {
                const newElement: ImageVariantElement = {
                    ...element,
                    variantType: VariantType.Image,
                    componentId: componentTypes?.map?.[VariantType.Image]?.id,
                    children: [{ text }],
                    id,
                    labelId,
                };
                const elementText =
                    (structure?.[elementIndex] as Element)?.children?.[0]
                        ?.text ?? '';
                if (elementText !== text) {
                    let updatedStructure = [...structure];
                    updatedStructure[elementIndex] = newElement;
                    return updatedStructure;
                }
                return;
            }
            return structure;
        },
        [element, getCurrentElementIndex, originalStructure, componentTypes],
    );

    const handleUpdateFormula = useCallback(
        async (
            originalVariantId: string,
            originalText: string,
            messageContentId: string,
            labelId?: number,
            callback?: () => Promise<void>,
        ) => {
            const updatedStructure = getUpdateStructure(
                originalVariantId,
                originalText,
                labelId,
            );

            if (updatedStructure) {
                await messageContentUpdate.mutateAsync({
                    id: messageContentId,
                    structure: updatedStructure,
                });
                handleUpdateStructure(updatedStructure);
            }

            await callback?.();
        },
        [getUpdateStructure, messageContentUpdate, handleUpdateStructure],
    );

    const redirectToFormulaDraft = useCallback(
        async (formulaId: number) => {
            if (isNewFormulaRef.current) {
                const currentStep = queryParams.current_step;
                const currentStepParam = currentStep
                    ? `&current_step=${currentStep}`
                    : '';
                history.replace({
                    pathname: routes.formulaDraft(formulaId),
                    search: `message_type=${formulaType}${currentStepParam}`,
                });
            }
        },
        [queryParams.current_step, history, formulaType],
    );

    const handleCreateMasterVariant = useCallback(
        (
            data: VariantUpdateModel,
            formulaId: number,
            messageContentId: string,
        ) => {
            const id = uuid();
            if (data.is_master) {
                handleUpdateFormula(
                    id,
                    data.text,
                    messageContentId!,
                    undefined,
                    () => redirectToFormulaDraft(formulaId!),
                );
            }
        },
        [handleUpdateFormula, redirectToFormulaDraft],
    );

    const handleAddAlternates = useCallback(
        async (data: VariantUpdateModel, messageContentId: string) => {
            let id: number = Number(formulaId);
            if (!formulaId) {
                const { data } = await createFormula.mutateAsync({
                    structure: FormulaTypesData[
                        message_type as FormulaType
                    ].getTemplate({ content_card_type: contentCardType }),
                });
                if (data) {
                    id = data.id;
                    isNewFormulaRef.current = true;
                }
            }

            handleCreateMasterVariant(
                data as VariantUpdateModel,
                id,
                messageContentId,
            );
        },
        [
            formulaId,
            handleCreateMasterVariant,
            createFormula,
            message_type,
            contentCardType,
        ],
    );

    const handleAdd = debounce(() => {
        handleAddAlternates(
            {
                id: 'Master_alternate',
                text: text!,
                variant_type: VariantType.Image,
                is_master: true,
                deleted: false,
                component_id: 'Master_alternate',
            },
            currentMessageContentId!,
        );
    }, 2500);

    const hasAlternates = useMemo<boolean>(
        () => data.some((item) => item.id !== item.component_id),
        [data],
    );

    const updateStructureForDataset = useCallback(
        async (id?: number, fieldText?: string) => {
            const structure = [...(originalStructure ?? [])];
            const elementIndex = getCurrentElementIndex();
            if (elementIndex !== -1) {
                const newElement = newImageElement(
                    id,
                    fieldText,
                    element,
                    datasetData,
                    isEmail,
                    inputType,
                );
                let updatedStructure = [...structure];
                updatedStructure[elementIndex] = newElement as Element;
                messageContentUpdate.mutate(
                    {
                        id: currentMessageContentId!,
                        structure: updatedStructure,
                    },
                    {
                        onSuccess: () => {
                            handleUpdateStructure(updatedStructure);
                        },
                    },
                );
            }
        },
        [
            originalStructure,
            getCurrentElementIndex,
            element,
            datasetData,
            messageContentUpdate,
            handleUpdateStructure,
            currentMessageContentId,
            isEmail,
            inputType,
        ],
    );

    const handleDatasetFieldClick = useCallback(
        async (id: number) => {
            if (hasAlternates) {
                const isConfirmed = await confirm(
                    'Attached image alternates will be removed.',
                    'warning',
                    'The image alternates will be removed once the dataset is added.',
                    undefined,
                    'Add dataset',
                );
                if (isConfirmed) {
                    updateStructureForDataset(id);
                }
            } else {
                updateStructureForDataset(id);
            }
        },
        [hasAlternates, updateStructureForDataset],
    );

    const handleClickDatasetIcon = useCallback(() => {
        setShowDatasetConfigurationalModal(true);
    }, []);

    return (
        <Container {...attributes}>
            <Body contentEditable={false}>
                <ImageInputLabel
                    isRequired={isRequired}
                    isEmail={isEmail}
                    inputType={inputType}
                    label={label}
                />
                <ImageInput
                    isDisabled={isDisabled}
                    handleClickDatasetIcon={handleClickDatasetIcon}
                    datasetData={datasetData}
                    element={element}
                    updateStructureForDataset={updateStructureForDataset}
                    hasWarning={hasWarning}
                    isEmail={isEmail}
                    handleAdd={handleAdd}
                    text={text}
                    setText={setText}
                    setShowImageAlternateIconModal={
                        setShowImageAlternateIconModal
                    }
                    handleDatasetFieldClick={handleDatasetFieldClick}
                    data={data}
                />
                {showImageAlternateIconModal && (
                    <ImageAlternateContainer
                        handleUpdateStructure={handleUpdateStructure}
                        setShowImageAlternateIconModal={
                            setShowImageAlternateIconModal
                        }
                        elementData={element}
                        showImageAlternateIconModal={
                            showImageAlternateIconModal
                        }
                        isEditable={!isDisabled}
                    />
                )}
                {showDatasetConfigurationalModal && (
                    <DatasetConfigurationalModalV1
                        handleClose={() => {
                            setShowDatasetConfigurationalModal(false);
                            setSelectedFieldId(undefined);
                        }}
                        showColumnSelect
                        handleColumnSelect={(id) => {
                            handleDatasetFieldClick(Number(id));
                        }}
                        selectedFieldId={selectedFieldId}
                        setSelectedFieldId={setSelectedFieldId}
                    />
                )}
            </Body>
        </Container>
    );
};

const Container = styled.div({
    padding: aampeTheme.spacing(0.5),
    display: 'flex',
    flexDirection: 'column',
});

const Body = styled.div({
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'column',
    rowGap: aampeTheme.spacing4(2),
});

export default ImageInputElement;
